import type { Equipment, Pipeline } from '@/types';

// 风电机组维护 3D 场景配置 v1
// 10 个关键设备 + 4 段主干连接
// 说明：本场景为 3D 渲染，x/y/width/height 仅用于复用 2D 数据结构，
// 真实 3D 空间位置在 src/scenes/windturbine/WindTurbine3D.tsx 内单独映射。

// 时间常数（秒）—— 风机是机械惯性较大的旋转系统
const TAU_PITCH = 3;      // 桨距角：变桨电机响应较快
const TAU_RPM = 8;        // 转速：大叶轮惯性大
const TAU_TEMP = 15;      // 温度类：齿轮箱油温、发电机绕组温升降都慢
const TAU_VIB = 2;        // 振动：响应快
const TAU_YAW = 12;       // 偏航：大质量机舱转动慢
const TAU_POWER = 5;      // 功率：变流器调节响应中等
const TAU_WIND = 1;       // 风速：气象变化快

export const windturbineEquipments: Equipment[] = [
  // ============= 1. 叶片 A — 桨距控制 =============
  {
    id: 'BLADE-A', name: '叶片A', type: 'robot',
    x: 540, y: 80, width: 80, height: 200,
    status: 'normal', template: 'windturbine',
    parameters: [
      { id: 'PITCH-A', name: '桨距角A', value: 8, unit: '°', min: 0, max: 90, normalMin: 0, normalMax: 15, trend: [], tau: TAU_PITCH },
      { id: 'RPM', name: '转速', value: 15, unit: 'rpm', min: 0, max: 30, normalMin: 10, normalMax: 20, trend: [], tau: TAU_RPM },
    ],
  },

  // ============= 2. 叶片 B — 桨距控制 =============
  {
    id: 'BLADE-B', name: '叶片B', type: 'robot',
    x: 640, y: 80, width: 80, height: 200,
    status: 'normal', template: 'windturbine',
    parameters: [
      { id: 'PITCH-B', name: '桨距角B', value: 8, unit: '°', min: 0, max: 90, normalMin: 0, normalMax: 15, trend: [], tau: TAU_PITCH },
      { id: 'RPM', name: '转速', value: 15, unit: 'rpm', min: 0, max: 30, normalMin: 10, normalMax: 20, trend: [], tau: TAU_RPM },
    ],
  },

  // ============= 3. 叶片 C — 桨距控制 =============
  {
    id: 'BLADE-C', name: '叶片C', type: 'robot',
    x: 740, y: 80, width: 80, height: 200,
    status: 'normal', template: 'windturbine',
    parameters: [
      { id: 'PITCH-C', name: '桨距角C', value: 8, unit: '°', min: 0, max: 90, normalMin: 0, normalMax: 15, trend: [], tau: TAU_PITCH },
      { id: 'RPM', name: '转速', value: 15, unit: 'rpm', min: 0, max: 30, normalMin: 10, normalMax: 20, trend: [], tau: TAU_RPM },
    ],
  },

  // ============= 4. 轮毂 — 振动与载荷 =============
  {
    id: 'HUB', name: '轮毂', type: 'station',
    x: 620, y: 280, width: 120, height: 80,
    status: 'normal', template: 'windturbine',
    parameters: [
      { id: 'VIB-HUB', name: '轮毂振动', value: 2.8, unit: 'mm/s', min: 0, max: 20, normalMin: 0, normalMax: 4.5, trend: [], tau: TAU_VIB },
      { id: 'LOAD', name: '载荷', value: 2.5, unit: 'MW', min: 0, max: 5, normalMin: 1.5, normalMax: 3.5, trend: [], tau: TAU_POWER },
    ],
  },

  // ============= 5. 齿轮箱 — 油温油压 =============
  {
    id: 'GEARBOX', name: '齿轮箱', type: 'main-drive',
    x: 400, y: 340, width: 140, height: 100,
    status: 'normal', template: 'windturbine',
    parameters: [
      { id: 'T-GB', name: '齿轮箱油温', value: 72, unit: '°C', min: 20, max: 150, normalMin: 50, normalMax: 80, trend: [], tau: TAU_TEMP },
      { id: 'P-GB', name: '齿轮箱油压', value: 3.5, unit: 'bar', min: 0, max: 10, normalMin: 2, normalMax: 5, trend: [], tau: 1 },
      { id: 'VIB-GB', name: '齿轮箱振动', value: 3.2, unit: 'mm/s', min: 0, max: 20, normalMin: 0, normalMax: 4.5, trend: [], tau: TAU_VIB },
    ],
  },

  // ============= 6. 发电机 — 绕组温度与功率 =============
  {
    id: 'GEN', name: '发电机', type: 'compressor',
    x: 280, y: 360, width: 100, height: 80,
    status: 'normal', template: 'windturbine',
    parameters: [
      { id: 'T-GEN', name: '绕组温度', value: 95, unit: '°C', min: 20, max: 200, normalMin: 60, normalMax: 120, trend: [], tau: TAU_TEMP },
      { id: 'POWER', name: '有功功率', value: 2.5, unit: 'MW', min: 0, max: 5, normalMin: 1.5, normalMax: 3.5, trend: [], tau: TAU_POWER },
    ],
  },

  // ============= 7. 偏航系统 — 对风角度 =============
  {
    id: 'YAW', name: '偏航系统', type: 'robot',
    x: 600, y: 420, width: 160, height: 60,
    status: 'normal', template: 'windturbine',
    parameters: [
      { id: 'YAW-POS', name: '偏航角度', value: 0, unit: '°', min: 0, max: 360, normalMin: 0, normalMax: 10, trend: [], tau: TAU_YAW },
      { id: 'YAW-RATE', name: '偏航速率', value: 0.5, unit: '°/s', min: 0, max: 2, normalMin: 0.1, normalMax: 1.0, trend: [], tau: 2 },
      // 偏航偏差：机舱朝向与风向的偏差角
      { id: 'YAW-ERR', name: '偏航偏差', value: 0, unit: '°', min: 0, max: 30, normalMin: 0, normalMax: 5, trend: [], tau: 3 },
    ],
  },

  // ============= 8. 变流器柜 — 直流母线与电网 =============
  {
    id: 'CONVERTER', name: '变流器柜', type: 'control_box',
    x: 200, y: 460, width: 120, height: 90,
    status: 'normal', template: 'windturbine',
    parameters: [
      { id: 'DC-VOLT', name: '直流母线电压', value: 1050, unit: 'V', min: 0, max: 1500, normalMin: 950, normalMax: 1200, trend: [], tau: 1 },
      { id: 'GRID-FREQ', name: '电网频率', value: 50, unit: 'Hz', min: 45, max: 55, normalMin: 49.5, normalMax: 50.5, trend: [], tau: 0.5 },
      // 直流母线电流：变流器直流侧电流监控
      { id: 'CURR', name: '母线电流', value: 800, unit: 'A', min: 0, max: 1500, normalMin: 600, normalMax: 900, trend: [], tau: 2 },
    ],
  },

  // ============= 9. 振动传感器 — 塔筒与叶片监测 =============
  {
    id: 'SENSOR-VIB', name: '振动传感器', type: 'instrument',
    x: 800, y: 360, width: 80, height: 60,
    status: 'normal', template: 'windturbine',
    parameters: [
      { id: 'VIB-TOWER', name: '塔筒振动', value: 1.2, unit: 'mm/s', min: 0, max: 15, normalMin: 0, normalMax: 2.5, trend: [], tau: TAU_VIB },
      { id: 'VIB-BLADE', name: '叶片振动', value: 1.8, unit: 'mm/s', min: 0, max: 15, normalMin: 0, normalMax: 3.0, trend: [], tau: TAU_VIB },
    ],
  },

  // ============= 10. 测风仪 — 风速风向 =============
  {
    id: 'WIND', name: '测风仪', type: 'instrument',
    x: 880, y: 60, width: 80, height: 60,
    status: 'normal', template: 'windturbine',
    parameters: [
      { id: 'WIND-SPD', name: '风速', value: 12, unit: 'm/s', min: 0, max: 40, normalMin: 3, normalMax: 25, trend: [], tau: TAU_WIND },
      { id: 'WIND-DIR', name: '风向', value: 0, unit: '°', min: 0, max: 360, normalMin: 0, normalMax: 360, trend: [], tau: TAU_WIND },
    ],
  },
];

// ============= 主干管路（4 段）=============
// 风机能量链：风能 → 叶片（旋转）→ 轮毂 → 齿轮箱（增速）→ 发电机（发电）→ 变流器（并网）
export const windturbinePipelines: Pipeline[] = [
  // 1. 风能：测风仪 → 叶片（蓝色，气流方向）
  { id: 'WT-P-01', from: 'WIND', to: 'BLADE-A', fromPoint: 'left', toPoint: 'right', medium: '风能', flowRate: 12, color: '#3b82f6' },
  // 2. 机械转矩：轮毂 → 齿轮箱（橙色，机械传动）
  { id: 'WT-P-02', from: 'HUB', to: 'GEARBOX', fromPoint: 'bottom', toPoint: 'top', medium: '机械转矩', flowRate: 2.5, color: '#f97316' },
  // 3. 电能：发电机 → 变流器（黄色，电力传输）
  { id: 'WT-P-03', from: 'GEN', to: 'CONVERTER', fromPoint: 'left', toPoint: 'right', medium: '电能', flowRate: 2.5, color: '#eab308' },
  // 4. 偏航对风：偏航系统 → 轮毂（青色，方位调节）
  { id: 'WT-P-04', from: 'YAW', to: 'HUB', fromPoint: 'top', toPoint: 'bottom', medium: '方位调节', flowRate: 0.5, color: '#06b6d4' },
];
